import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select } from 'antd';
import Pagination from '../../components/Pagination';
import {
  NewEngagementIcon,
  SearchIcon,
  DownloadIcon,
  PublicAdminIcon,
  CaretIcon,
} from '../../assets/icons';

type Priority = 'critical' | 'high' | 'medium' | 'low';
type Status = 'new' | 'inprogress' | 'overdue' | 'completed';

interface Request {
  cr: string;
  name: string;
  subject: string;
  category: string;
  priority: Priority;
  status: Status;
  date: string;
  owner: string;
  dateOverdue?: boolean;
}

const REQUESTS: Request[] = [
  { cr: 'CR1023', name: 'Ramesh', subject: 'Drinking Water', category: 'Grievance', priority: 'high', status: 'inprogress', date: '25-Jul', owner: 'Ravi' },
  { cr: 'CR1024', name: 'Suresh', subject: 'Wedding Invitation', category: 'Invitation', priority: 'medium', status: 'new', date: '27-Jul', owner: 'Ankit' },
  { cr: 'CR1025', name: 'Lakshmi', subject: 'Pension Issue', category: 'Public', priority: 'critical', status: 'overdue', date: '22-Jul', owner: 'Kavi', dateOverdue: true },
  { cr: 'CR1026', name: 'Mohan', subject: 'Transfer Request', category: 'Sifarish', priority: 'medium', status: 'completed', date: '20-Jul', owner: 'Ram' },
];

const PRIORITY_META: Record<Priority, { label: string; dot: string }> = {
  critical: { label: 'CRITICAL', dot: 'bg-[#ef4444]' },
  high: { label: 'HIGH', dot: 'bg-[#ef4444]' },
  medium: { label: 'MEDIUM', dot: 'bg-[#f59e0b]' },
  low: { label: 'LOW', dot: 'bg-[#94a3b8]' },
};

const STATUS_META: Record<Status, { label: string; cls: string }> = {
  new: { label: 'NEW', cls: 'bg-[#f1f5f9] text-[#475569]' },
  inprogress: { label: 'IN PROGRESS', cls: 'border border-[#bfdbfe] bg-[#eff6ff] text-brand' },
  overdue: { label: 'OVERDUE', cls: 'bg-[#ef4444] text-white' },
  completed: { label: 'COMPLETED', cls: 'bg-[#dcfce7] text-[#15803d]' },
};

/** Categories that get the brand-blue chip; the rest are neutral grey. */
const BLUE_CATEGORIES = new Set(['Invitation', 'Public']);
const AVATAR_TINTS = [
  'bg-[#dbeafe] text-[#0061ff]',
  'bg-[#e0e7ff] text-[#4f46e5]',
  'bg-[#f3e8ff] text-[#9333ea]',
  'bg-[#fee2e2] text-[#dc2626]',
];

const GRID =
  'grid grid-cols-[90px_170px_minmax(0,1fr)_130px_120px_140px_120px_80px] items-center';
const TH = 'text-[11px] font-bold uppercase tracking-[0.5px] text-[#94a3b8]';

interface StatCard {
  label: string;
  value: string;
  valueColor?: string;
  extra: ReactNode;
}

const STAT_CARDS: StatCard[] = [
  {
    label: 'Critical Escalations',
    value: '12',
    valueColor: 'text-[#dc2626]',
    extra: (
      <span className="rounded bg-[#fee2e2] px-1.5 py-0.5 text-[11px] font-semibold text-[#dc2626]">
        +4 today
      </span>
    ),
  },
  {
    label: 'Active Requests',
    value: '84',
    extra: <span className="text-xs text-ink-secondary">62% processing</span>,
  },
  {
    label: 'Completed (MTD)',
    value: '1,240',
    extra: <span className="h-1 w-10 rounded-full bg-brand" />,
  },
  {
    label: 'Avg. Response Time',
    value: '4.2h',
    extra: <span className="text-sm font-normal leading-5 text-[#6b7280]">-12% vs last week</span>,
  },
];

const FILTERS: { label: string; placeholder: string; options: string[] }[] = [
  { label: 'Subject', placeholder: 'All Subject', options: ['All Subject'] },
  { label: 'Category', placeholder: 'All Categories', options: ['All Categories', 'Grievance', 'Invitation', 'Public', 'Sifarish'] },
  { label: 'Priority', placeholder: 'Any Priority', options: ['Any Priority', 'Critical', 'High', 'Medium', 'Low'] },
  { label: 'Status', placeholder: 'All Statuses', options: ['All Statuses', 'New', 'In Progress', 'Overdue', 'Completed'] },
  { label: 'Date Range', placeholder: 'Last 30 Days', options: ['Last 30 Days', 'Last 7 Days', 'This Year'] },
  { label: 'Source', placeholder: 'All Sources', options: ['All Sources', 'Web', 'Field', 'Phone'] },
];

function initials(name: string) {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? '') + (p[1]?.[0] ?? '')).toUpperCase() || '—';
}

export default function CitizenRequestsPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const total: number = 1250;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.6px] text-brand">
            <PublicAdminIcon className="h-3 w-[15px]" />
            Public Administration
          </span>
          <h1 className="text-2xl font-semibold text-ink">Citizen Requests</h1>
          <p className="text-sm text-ink-secondary">
            Manage and monitor community grievances, invitations, and service requests.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg border border-line bg-white px-4 py-2.5 text-sm font-medium text-ink-secondary transition-colors hover:bg-brand-shade"
          >
            <DownloadIcon className="size-4" />
            Export Data
          </button>
          <button
            type="button"
            onClick={() => navigate('/user/citizen-requests/new')}
            className="flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark"
          >
            <NewEngagementIcon className="size-4" />
            Create New CR
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {STAT_CARDS.map((c) => (
          <div
            key={c.label}
            className="flex flex-col gap-3 rounded-xl border border-[#f1f5f9] bg-white p-5 shadow-card"
          >
            <span className={TH}>{c.label}</span>
            <div className="flex items-end gap-3">
              <span className={['text-4xl font-bold leading-none', c.valueColor ?? 'text-brand'].join(' ')}>
                {c.value}
              </span>
              <span className="mb-1 flex items-center">{c.extra}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex flex-col gap-4 rounded-xl border border-[#f1f5f9] bg-white p-5 shadow-card">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#9ca3af]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search commands, IDs, or citizen names..."
              className="w-full rounded-lg bg-[#f8fafc] py-2.5 pl-10 pr-3 text-sm text-ink placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
          </div>
          <button type="button" className="shrink-0 text-sm font-semibold text-brand hover:underline">
            Clear All
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
          {FILTERS.map((f) => (
            <div key={f.label} className="flex flex-col gap-1.5">
              <span className={TH}>{f.label}</span>
              <Select
                className="w-full"
                defaultValue={f.placeholder}
                options={f.options.map((o) => ({ value: o, label: o }))}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-[#f1f5f9] bg-white shadow-card">
        <div className="overflow-x-auto">
          <div className="min-w-[960px]">
            {/* Header */}
            <div className={[GRID, 'border-b border-[#f1f5f9] bg-[#f8fafc] px-6 py-3.5'].join(' ')}>
              <span className={['flex items-center gap-1', TH].join(' ')}>
                CR No
                <span className="flex flex-col text-[#cbd5e1]">
                  <CaretIcon className="size-2.5 -rotate-180" />
                  <CaretIcon className="-mt-1 size-2.5" />
                </span>
              </span>
              <span className={TH}>Citizen Name</span>
              <span className={TH}>Subject</span>
              <span className={TH}>Category</span>
              <span className={TH}>Priority</span>
              <span className={TH}>Status</span>
              <span className={TH}>Requested Date</span>
              <span className={TH}>Owner</span>
            </div>

            {/* Rows */}
            {REQUESTS.map((r, i) => {
              const prio = PRIORITY_META[r.priority];
              const status = STATUS_META[r.status];
              return (
                <div
                  key={r.cr}
                  className={[GRID, 'px-6 py-4 transition-colors hover:bg-[#f8fafc]', i > 0 ? 'border-t border-[#f9fafb]' : ''].join(' ')}
                >
                  <button type="button" className="text-left text-sm font-semibold text-brand hover:underline">
                    {r.cr}
                  </button>
                  <div className="flex items-center gap-2.5">
                    <span className={['flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold', AVATAR_TINTS[i % AVATAR_TINTS.length]].join(' ')}>
                      {initials(r.name)}
                    </span>
                    <span className="text-sm font-medium text-[#334155]">{r.name}</span>
                  </div>
                  <span className="truncate pr-3 text-sm text-ink">{r.subject}</span>
                  <div>
                    <span
                      className={[
                        'inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium',
                        BLUE_CATEGORIES.has(r.category) ? 'bg-brand-shade text-brand' : 'bg-[#f1f5f9] text-[#475569]',
                      ].join(' ')}
                    >
                      {r.category}
                    </span>
                  </div>
                  <span className="flex items-center gap-2">
                    <span className={['size-2 rounded-full', prio.dot].join(' ')} />
                    <span className="text-xs font-semibold uppercase tracking-[0.3px] text-[#475569]">
                      {prio.label}
                    </span>
                  </span>
                  <div>
                    <span className={['inline-flex items-center rounded-md px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.3px]', status.cls].join(' ')}>
                      {status.label}
                    </span>
                  </div>
                  <span className={['text-sm font-medium', r.dateOverdue ? 'text-[#dc2626]' : 'text-[#475569]'].join(' ')}>
                    {r.date}
                  </span>
                  <div>
                    <span className="inline-flex items-center rounded-full bg-[#f1f5f9] px-2.5 py-1 text-xs font-medium text-[#334155]">
                      {r.owner}
                    </span>
                  </div>
                </div>
              );
            })}

            {/* Footer */}
            <Pagination
              rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
              rangeEnd={Math.min(page * pageSize, total)}
              total={total}
              noun="results"
              page={page}
              totalPages={Math.max(1, Math.ceil(total / pageSize))}
              onChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={(v) => {
                setPageSize(v);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
